/**
 * Family setup — first-run onboarding.
 *
 * Two-step flow inside a single screen:
 *   1. Parent enters family name + their own name + locale (EN/NL)
 *   2. Parent adds the first child (name + age + avatar)
 *
 * After step 2 the device is automatically "entered" as the child and
 * routed to the map. Additional children can be added later from the
 * parent dashboard.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useSignal } from '@preact/signals';
import { api } from '../lib/api';
import { enterAsUser } from '../state/user';

interface AvatarSpec {
  id: number;
  nameAr: string;
  nameEn: string;
  bg: string;
}

export function FamilySetup() {
  const { route } = useLocation();
  const step = useSignal<'family' | 'child'>('family');

  const familyName = useSignal('');
  const parentName = useSignal('');
  const locale = useSignal<'en' | 'nl'>('en');
  const familyId = useSignal<string | null>(null);

  const childName = useSignal('');
  const childAge = useSignal<number>(7);
  const [avatars, setAvatars] = useState<AvatarSpec[]>([]);
  const pickedAvatar = useSignal<number | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch('/assets/avatars/manifest.json')
      .then((r) => r.json() as Promise<AvatarSpec[]>)
      .then((list) => setAvatars(list))
      .catch(() => setAvatars([]));
  }, []);

  async function submitFamily(e: Event) {
    e.preventDefault();
    if (!familyName.value || !parentName.value) {
      setErr('ا‍ِمْلَأِ الحُقُولَ كُلَّهَا');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const resp = await api<{ ok: true; familyId: string }>('/api/family', {
        method: 'POST',
        body: {
          familyName: familyName.value,
          parentName: parentName.value,
          locale: locale.value,
        },
      });
      familyId.value = resp.familyId;
      step.value = 'child';
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitChild(e: Event) {
    e.preventDefault();
    if (!childName.value || pickedAvatar.value === null || !familyId.value) {
      setErr('اِخْتَرْ اِسْمًا وَصُورَةً');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const resp = await api<{ ok: true; user: { id: string } }>(
        `/api/family/${familyId.value}/member`,
        {
          method: 'POST',
          body: {
            name: childName.value,
            age: childAge.value,
            role: 'child',
            avatar: String(pickedAvatar.value),
          },
        },
      );
      await enterAsUser(resp.user.id);
      route('/map');
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (step.value === 'family') {
    return (
      <form
        onSubmit={submitFamily}
        class="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-6 max-w-md mx-auto w-full"
      >
        <h1 class="text-4xl font-display text-coral text-center">العَائِلَةُ</h1>
        <p class="text-sm text-ink/60 text-center" dir="ltr">
          Set up your family to start learning Arabic together.
        </p>

        <label class="w-full">
          <span class="block text-sm mb-1" dir="ltr">
            Family name
          </span>
          <input
            class="w-full min-h-[60px] px-4 py-3 rounded-2xl bg-white shadow-sm border border-ink/10 text-lg"
            value={familyName.value}
            onInput={(e) => (familyName.value = (e.target as HTMLInputElement).value)}
            placeholder="The Hassan Family"
            dir="ltr"
          />
        </label>

        <label class="w-full">
          <span class="block text-sm mb-1" dir="ltr">
            Your name (parent)
          </span>
          <input
            class="w-full min-h-[60px] px-4 py-3 rounded-2xl bg-white shadow-sm border border-ink/10 text-lg"
            value={parentName.value}
            onInput={(e) => (parentName.value = (e.target as HTMLInputElement).value)}
            placeholder="Amira"
            dir="ltr"
          />
        </label>

        <label class="w-full">
          <span class="block text-sm mb-1" dir="ltr">
            Parent UI language
          </span>
          <div class="grid grid-cols-2 gap-2">
            {(['en', 'nl'] as const).map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => (locale.value = loc)}
                class={`min-h-[60px] rounded-2xl border-2 font-bold ${
                  locale.value === loc
                    ? 'border-coral bg-coral/10 text-coral'
                    : 'border-ink/10 bg-white text-ink'
                }`}
              >
                {loc === 'en' ? 'English' : 'Nederlands'}
              </button>
            ))}
          </div>
        </label>

        {err && <p class="text-sm text-berry">{err}</p>}

        <button
          type="submit"
          disabled={busy}
          class="w-full min-h-[64px] px-8 py-3 rounded-2xl bg-coral text-white shadow-xl shadow-coral/40 active:scale-95 transition-transform disabled:opacity-60 flex flex-col items-center"
        >
          <span class="text-xl font-display">{busy ? '…' : 'التَّالِي'}</span>
          <span class="text-[10px] opacity-85" dir="ltr">Next</span>
        </button>
      </form>
    );
  }

  // Step 2: child
  return (
    <form
      onSubmit={submitChild}
      class="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-6 max-w-md mx-auto w-full"
    >
      <h1 class="text-4xl font-display text-coral text-center">الطِّفْلُ</h1>
      <p class="text-sm text-ink/60 text-center" dir="ltr">
        Add your first child.
      </p>

      <label class="w-full">
        <span class="block text-sm mb-1" dir="ltr">
          Child name
        </span>
        <input
          class="w-full min-h-[60px] px-4 py-3 rounded-2xl bg-white shadow-sm border border-ink/10 text-lg"
          value={childName.value}
          onInput={(e) => (childName.value = (e.target as HTMLInputElement).value)}
          placeholder="Yara"
          dir="ltr"
        />
      </label>

      <label class="w-full">
        <span class="block text-sm mb-1" dir="ltr">
          Age: {childAge.value}
        </span>
        <input
          type="range"
          min={5}
          max={12}
          value={childAge.value}
          onInput={(e) => (childAge.value = Number((e.target as HTMLInputElement).value))}
          class="w-full accent-coral"
        />
      </label>

      <div class="w-full">
        <span class="block text-sm mb-2" dir="ltr">
          Pick an avatar
        </span>
        <div class="grid grid-cols-3 gap-3">
          {avatars.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => (pickedAvatar.value = a.id)}
              class={`aspect-square rounded-2xl p-2 border-4 ${
                pickedAvatar.value === a.id
                  ? 'border-coral shadow-lg shadow-coral/30'
                  : 'border-transparent'
              }`}
            >
              <img
                src={`/assets/avatars/${a.id}.svg`}
                alt={a.nameEn}
                class="w-full h-full rounded-xl"
              />
              <span class="block text-xs mt-1 font-body">{a.nameAr}</span>
            </button>
          ))}
        </div>
      </div>

      {err && <p class="text-sm text-berry">{err}</p>}

      <button
        type="submit"
        disabled={busy}
        class="w-full min-h-[64px] px-8 py-3 rounded-2xl bg-coral text-white shadow-xl shadow-coral/40 active:scale-95 transition-transform disabled:opacity-60 flex flex-col items-center"
      >
        <span class="text-xl font-display">{busy ? '…' : 'هَيَّا نَبْدَأُ'}</span>
        <span class="text-[10px] opacity-85" dir="ltr">Let's begin</span>
      </button>
    </form>
  );
}
